import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'motion/react'
import { Cookie } from 'lucide-react'
import { seznamRetargetingHit } from '../lib/seznamRetargeting'
import { updateGoogleConsent } from '../lib/googleAnalytics'

/**
 * GDPR cookie consent banner — zobrazí se, dokud uživatel nezvolí.
 * Volba se ukládá do localStorage ('cookie_consent' = 'all' | 'essential').
 * Neesenciální cookies (analytika apod.) se mají spouštět až po souhlasu 'all'.
 */
export function getCookieConsent(): 'all' | 'essential' | null {
  const v = localStorage.getItem('cookie_consent')
  return v === 'all' || v === 'essential' ? v : null
}

export default function CookieConsent() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!getCookieConsent()) setOpen(true)
  }, [])

  const choose = (choice: 'all' | 'essential') => {
    localStorage.setItem('cookie_consent', choice)
    localStorage.setItem('cookie_consent_at', new Date().toISOString())
    setOpen(false)
    // Aktualizuj Google Consent Mode podle volby (granted jen u „Přijmout vše").
    updateGoogleConsent(choice === 'all')
    // Po udělení plného souhlasu odpálit Seznam retargeting hned (jinak až při příští navigaci).
    if (choice === 'all') seznamRetargetingHit()
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.25 }}
          className="cookie-consent fixed bottom-4 inset-x-4 sm:left-auto sm:right-6 sm:max-w-md z-[300]"
          role="dialog" aria-live="polite" aria-label={t('cookie.title')}
        >
          <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white/95 dark:bg-zinc-900/95 backdrop-blur shadow-2xl p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-primary-50 dark:bg-primary-500/15 text-primary-600 flex items-center justify-center shrink-0">
                <Cookie className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-900 dark:text-white">{t('cookie.title')}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('cookie.text')}{' '}
                  <Link to="/zasady-ochrany-osobnich-udaju" className="underline hover:text-primary-600">
                    {t('cookie.more')}
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => choose('essential')}
                className="flex-1 px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition">
                {t('cookie.essential')}
              </button>
              <button onClick={() => choose('all')}
                className="flex-1 px-3 py-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 text-white text-sm font-semibold hover:shadow-lg transition">
                {t('cookie.accept')}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
